import React from 'react';
import styled from 'styled-components';
import {useTranslation} from 'react-i18next';
import {ControlButton, TscButtonType} from '@techsmith/tsc-cloud-style-guide';
import LocalMediaImporter from './LocalMediaImporter';
import UploadProgressModal from './UploadProgressModal';
import Axios, {AxiosProgressEvent, CancelTokenSource} from 'axios';
import '../../../static/css/modal.less';
import cssConstants from '../../../constants/cssConstants';
import audioDescriptionApi from '../../../service/audioDescriptionApi';
import WarningWithFaqLink from '../../util/WarningWithFaqLink';
import {IFileUploadCandidate} from '../../../model/fileUploadCandidate';
import withMemoizedContexts from '../../../context/contextContainerHoC';
import {useTscTheme} from '../../../context/themeProvider';
import {mediaStore} from '../../../context/mediaProvider';
import {AlertModalPortal, BasicModalPortal} from '../../util/ModalPortalHelper';
import config from '../../../service/config';
import Constants from '../../../constants/Constants';

const MediaPreviewWrapper = styled.div`
   display: flex;
   object-fit: contain;
   justify-content: center;
   flex-direction: column;
   max-height: 30rem;
   max-width: 30rem;
   margin-bottom: 1rem;
   padding: 1rem;
`;

export const AudioPreview = styled.audio`
   max-height: ${cssConstants.defaultModalWidth};
   max-width: ${cssConstants.defaultModalWidth};
   height: 2.5rem;
   width: 100%;
   margin-bottom: 1rem;
`;

const ButtonGroup = styled.div`
   margin-top: 1rem;
`;

export const UploadAudioDescriptionModalBase: React.FC<IUploadAudioDescriptionModalProps & IStateMappedProps> = ({isReplacing, onClose, Hash}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const [state, setState] = React.useState({
      cancelTokenSource: null,
      errorText: '',
      file: null,
      fileSelected: false,
      fileUploading: false,
      showConfirmationModal: false,
      showFileSelectionModal: true,
      showUploadProgressModal: false,
      uploadProgress: 0
   } as IUploadAudioDescriptionModalState);

   const updateState = (updatedValues: Partial<IUploadAudioDescriptionModalState>): void => {
      setState(prevState => Object.assign({}, prevState, updatedValues));
   };

   const onUploadProgress = (progressEvent: AxiosProgressEvent): void => {
      const near100Percent = 95;
      const percentCompleted = Math.min(Math.round((progressEvent.loaded * 100) / progressEvent.total), near100Percent);
      updateState({uploadProgress: percentCompleted});
   };

   const onFileSelect = (uploadCandidate: IFileUploadCandidate) => {
      updateState({fileSelected: true, file: uploadCandidate.file});
   };

   const confirmReplaceMediaClick = (): void => {
      updateState({showConfirmationModal: true});
   };

   const replaceMedia = async (): Promise<void> => {
      try {
         const cancelTokenSource = Axios.CancelToken.source();
         updateState({cancelTokenSource: cancelTokenSource});
         await audioDescriptionApi.uploadAudioDescription(Hash, state.file, onUploadProgress, cancelTokenSource);
         updateState({uploadProgress: 100, fileUploading: false});
         window.location.reload();
      } catch (e) {
         updateState({showConfirmationModal: false, fileUploading: false, uploadProgress: 0, errorText: Axios.isCancel(e) ? '' : t('accessibility.audioDescription.modal.error'), showUploadProgressModal: false, showFileSelectionModal: true});
      }
   };

   const replaceMediaClick = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      updateState({showConfirmationModal: false, showUploadProgressModal: true, showFileSelectionModal: false, fileUploading: true});
      await replaceMedia();
   };

   const onCancelClick = (): void => {
      if (state.file && state.fileSelected) {
         updateState({file: null, fileSelected: false, errorText: ''});
      } else {
         onClose();
      }
   };

   const onFileSelectionModalClose = (): void => {
      updateState({showFileSelectionModal: false, showUploadProgressModal: state.fileUploading});
      !state.fileUploading && onClose();
   };

   const onConfirmationModalClose = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
      e.stopPropagation();
      updateState({showConfirmationModal: false});
   };

   const onUploadProgressModalClose = (): void => {
      if (!state.fileUploading && state.uploadProgress === 100) {
         onClose();
      } else {
         updateState({showUploadProgressModal: false, showFileSelectionModal: true});
      }
   };

   const handleUploadCancelRequest = (): void => {
      state.cancelTokenSource?.cancel();
   };

   const getWarningWithFaqLinkUrl = (): string => {
      if (config.featureSwitches.IsScreencast) {
         return Constants.navigation.screencastFAQLink;
      }
      return Constants.navigation.knowmiaFAQLink;
   };

   const buttonGroup: React.JSX.Element = (
      <ButtonGroup className="button-group">
         <ControlButton
            label={state.fileSelected ? t('general.back') : t('general.cancel')}
            buttonType={TscButtonType.secondary}
            disabled={false}
            themeName={theme}
            width={cssConstants.defaultButtonWidth}
            onClick={onCancelClick}
            testId="replace-audio-description-modal-cancel-button"
         />
         {state.fileSelected && (
            <ControlButton
               label={t(isReplacing ? 'accessibility.audioDescription.modal.replace' : 'accessibility.audioDescription.modal.upload')}
               buttonType={TscButtonType.primary}
               disabled={!state.fileSelected}
               themeName={theme}
               width={cssConstants.defaultButtonWidth}
               onClick={isReplacing ? confirmReplaceMediaClick : replaceMediaClick}
               testId="replace-audio-description-modal-upload-button"
            />
         )}
      </ButtonGroup>
   );

   const mediaPreview: React.JSX.Element =
      state.file && (
         <MediaPreviewWrapper>
            <AudioPreview
               src={URL.createObjectURL(state.file)}
               controls={true}
               autoPlay={false}
            />
            <span>
               {state.file.name}
            </span>
         </MediaPreviewWrapper>
      );

   const fileSelectModal: React.JSX.Element = (
      <BasicModalPortal
         visible={true}
         title={t('accessibility.audioDescription.modal.title')}
         onClose={onFileSelectionModalClose}
         themeName={theme}
         testId="replace-media-modal"
      >
         <div className="modal-body">
            <div className="form-body themeable-section">
               {state.errorText && (
                  <WarningWithFaqLink linkUrl={getWarningWithFaqLinkUrl()}>
                     <div>
                        {state.errorText}
                     </div>
                  </WarningWithFaqLink>
               )}
               {!state.fileSelected && (
                  <LocalMediaImporter
                     onFileSelect={onFileSelect}
                     isAudioUploader={true}
                     allowMultiple={false}
                  />
               )}
               {(state.fileSelected && !state.fileUploading) && mediaPreview}
               {buttonGroup}
            </div>
         </div>
      </BasicModalPortal>
   );

   const replaceConfirmationAlertModal: React.JSX.Element = (
      <AlertModalPortal
         title={t('accessibility.audioDescription.replaceConfirmation.title')}
         onClose={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => onConfirmationModalClose(e)}
         onConfirm={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => replaceMediaClick(e)}
         confirmButtonType={TscButtonType.destructive}
         confirmContent={t('general.confirm')}
         onDismiss={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => onConfirmationModalClose(e)}
         dismissButtonType={TscButtonType.secondary}
         dismissContent={t('general.cancel')}
      >
         {t('accessibility.audioDescription.replaceConfirmation.text')}
      </AlertModalPortal>
   );

   return (
      <>
         {state.showFileSelectionModal && fileSelectModal}
         {state.showConfirmationModal && replaceConfirmationAlertModal}
         {state.showUploadProgressModal && (
            <UploadProgressModal
               uploadProgress={state.uploadProgress}
               cancelUpload={handleUploadCancelRequest}
               onClose={onUploadProgressModalClose}
            />
         )}
      </>
   );
};

export interface IUploadAudioDescriptionModalProps {
   isReplacing: boolean;
   onClose(): void;
}

interface IUploadAudioDescriptionModalState {
   cancelTokenSource: CancelTokenSource;
   errorText: string;
   file: File;
   fileSelected: boolean;
   fileUploading: boolean;
   showConfirmationModal: boolean;
   showFileSelectionModal: boolean;
   showUploadProgressModal: boolean;
   uploadProgress: number;
}

export interface IStateMappedProps {
   Hash: string;
}

const mapStatesToProps = (
   {isReplacing, onClose}: IUploadAudioDescriptionModalProps,
   {Hash}: Partial<IStateMappedProps>
): IStateMappedProps & IUploadAudioDescriptionModalProps => ({isReplacing, onClose, Hash});

export default withMemoizedContexts(mapStatesToProps, mediaStore)(UploadAudioDescriptionModalBase);
